import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import {
  type PartnerCreateSchema,
  type PartnerQueryAllSchema,
  type PartnerQuerySchema,
  type PartnerUpdateSchema,
} from './schemas';

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, PartnerCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/partners/',
        payload,
      }),
  });

export function useGetPartners(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['PARTNERS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<PartnerQueryAllSchema>({
        endpoint: '/api/v1/partners',
        params: `${apiParamStr}&include=icon,logo`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PARTNERS'] });
};

export const useGetPartnersMutation: UseControlledMutation<PartnerQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['PARTNERS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<PartnerQueryAllSchema>({
            endpoint: '/api/v1/partners',
            params: { search, include: 'icon', per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetPartner(id: string) {
  return useSuspenseQuery({
    queryKey: ['PARTNER', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PartnerQuerySchema }>({
        endpoint: `/api/v1/partners/${id}`,
        params: { include: 'icon,logo,logo_light,invoices' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: PartnerQuerySchema }, ApiError, Partial<PartnerUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/partners/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PARTNER_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/partners/${id}`,
        method: 'DELETE',
      }),
  });
